import { PipelineDataType } from "encamp-prisma/generated/browser";
import { z } from "zod";

export type InvoiceVendor = "CLEAN_EARTH" | "REPUBLIC";

export type InvoicePipelineFileSlot = {
  key: "source";
  label: string;
  acceptedExtensions: readonly string[];
  required: true;
};

export type InvoicePipelineConfig = {
  label: string;
  vendor: InvoiceVendor;
  canonicalVendorName: string;
  dataType: typeof PipelineDataType.WASTE_INVOICE;
  files: readonly InvoicePipelineFileSlot[];
};

export const INVOICE_PIPELINE_NAMES: readonly ["clean-earth-invoices", "republic-invoices"] = [
  "clean-earth-invoices",
  "republic-invoices",
];

export type InvoicePipelineName = (typeof INVOICE_PIPELINE_NAMES)[number];

/**
 * Vendor-neutral invoice pipeline identities. The vendor modules are registered
 * independently by the owning vendor layers, so adding metadata here never
 * creates a dispatch path to a module that does not exist yet.
 */
export const INVOICE_PIPELINE_REGISTRY = {
  "clean-earth-invoices": {
    label: "Clean Earth invoices",
    vendor: "CLEAN_EARTH",
    canonicalVendorName: "Clean Earth",
    dataType: PipelineDataType.WASTE_INVOICE,
    files: [
      {
        key: "source",
        label: "Clean Earth invoice workbook",
        acceptedExtensions: [".xlsx"],
        required: true,
      },
    ],
  },
  "republic-invoices": {
    label: "Republic invoices",
    vendor: "REPUBLIC",
    canonicalVendorName: "Republic",
    dataType: PipelineDataType.WASTE_INVOICE,
    files: [
      {
        key: "source",
        label: "Republic invoice workbook",
        acceptedExtensions: [".xls", ".xlsx"],
        required: true,
      },
    ],
  },
} satisfies Record<InvoicePipelineName, InvoicePipelineConfig>;

export function isInvoicePipelineName(
  parameters: unknown
): parameters is { pipelineName: InvoicePipelineName } {
  const result = z.object({ pipelineName: z.string() }).safeParse(parameters);
  return (
    result.success &&
    Object.prototype.hasOwnProperty.call(INVOICE_PIPELINE_REGISTRY, result.data.pipelineName)
  );
}

export function normalizeFileExtension(fileNameOrExtension: string): string {
  const normalized = fileNameOrExtension.trim().toLowerCase();
  const lastDot = normalized.lastIndexOf(".");
  return lastDot >= 0 ? normalized.slice(lastDot) : `.${normalized}`;
}

export function isSupportedInvoiceFileExtension(
  pipelineName: InvoicePipelineName,
  fileNameOrExtension: string
): boolean {
  const extension = normalizeFileExtension(fileNameOrExtension);
  return INVOICE_PIPELINE_REGISTRY[pipelineName].files[0].acceptedExtensions.some(
    (acceptedExtension) => acceptedExtension === extension
  );
}
